import { createContext, useState, useContext, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [actualUser, setActualUser] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });

      if (response.data.status === "success") {
        setUser(response.data.user);
        setIsImpersonating(response.data.is_impersonating);
        setActualUser(response.data.actual_user);
      }
    } catch (error) {
      setUser(null);
      setIsImpersonating(false);
      setActualUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    setUser(userData);
    setLoading(false);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      setIsImpersonating(false);
      setActualUser(null);
    }
  };

  const startImpersonation = (targetUser) => {
    setIsImpersonating(true);
    setActualUser({ ...user });
    setUser(targetUser);
  };

  const exitImpersonation = () => {
    setUser(actualUser);
    setIsImpersonating(false);
    setActualUser(null);
  };

  const value = {
    user,
    loading,
    isImpersonating,
    actualUser,
    login,
    logout,
    checkAuth,
    startImpersonation,
    exitImpersonation
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
